import { useEffect } from 'react';
import { useTenantStore } from '@/store/tenant.store';
import { useBrandingStore } from '@/store/branding.store';
import { toApiUrl } from '@/utils/url';

const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * Every branding field the store can carry, reset to the built-in defaults.
 *
 * WHY this exists: `useBrandingStore.setBranding` MERGES a partial into the
 * existing state (`set({ ...branding, isLoaded: true })`) and then re-applies
 * the result to the DOM and to the PWA manifest link. So a bail-out branch that
 * only writes `{ tenantSlug, tenantId }` silently keeps the PREVIOUS tenant's
 * seal, palette and app name while relabelling them with the NEW tenant's slug.
 *
 * That is not cosmetic. An operator supporting two jurisdictions switches from
 * County A to County B; the branding fetch fails (network blip, 500/502/503, or
 * a 429 from the public route's rate limiter — anything non-404 throws below).
 * County A's logo and name then stay on the chrome, on printed credentials
 * (CredentialQRTab) and burned into exported map products (MapExportDialog)
 * while County B's records are being created. And because the effect's guard is
 * `activeTenant.slug === tenantSlug`, it never refetches, and `partialize`
 * persists the mislabelled branding into future sessions.
 *
 * Values must match the store's own initial state (branding.store.ts) exactly.
 */
const BRANDING_RESET = {
  logoUrl: null,
  thumbnailUrl: null,
  colors: null,
  typography: null,
  appName: 'Command Bridge',
  pwaShortName: 'CmdBridge',
} as const;

interface PublicBrandingResponse {
  branding: {
    logo_url?: string;
    colors?: {
      primary?: string;
      secondary?: string;
      accent?: string;
      background?: string;
      surface?: string;
      text_primary?: string;
      text_secondary?: string;
    };
    typography?: {
      font_family?: string;
      heading_weight?: string;
      body_weight?: string;
    };
    app_name?: string;
    pwa_short_name?: string;
  };
  tenant_id: string;
  cache_ttl: number;
}

/**
 * Hook to initialize tenant branding on app load and tenant switch.
 * Fetches branding from public endpoint and applies to the UI.
 */
export function useBrandingInit(): void {
  const activeTenant = useTenantStore((state) => state.activeTenant);
  const { tenantSlug, setBranding, clearBranding } = useBrandingStore();

  useEffect(() => {
    async function loadBranding() {
      // No active tenant, clear branding
      if (!activeTenant?.slug) {
        clearBranding();
        return;
      }

      // Already loaded for this tenant
      if (activeTenant.slug === tenantSlug) {
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/public/branding/${activeTenant.slug}`);

        if (!response.ok) {
          // Tenant doesn't have custom branding, use defaults
          if (response.status === 404) {
            // Reset to defaults in ONE setBranding call — a bare
            // { tenantSlug, tenantId } would leave the previous tenant's
            // branding applied under this tenant's slug (see BRANDING_RESET).
            setBranding({
              ...BRANDING_RESET,
              tenantSlug: activeTenant.slug,
              tenantId: activeTenant.id,
            });
            return;
          }
          throw new Error(`Failed to fetch branding: ${response.status}`);
        }

        const data: PublicBrandingResponse = await response.json();

        // Transform API response to store format
        // Prepend API base URL to relative logo paths
        const logoUrl = toApiUrl(data.branding.logo_url);

        setBranding({
          logoUrl,
          // Explicitly cleared: the public branding response carries no
          // thumbnail, so without this the previous tenant's thumbnailUrl
          // (written by BrandingSettings) survives every tenant switch.
          thumbnailUrl: null,
          colors: data.branding.colors
            ? {
                primary: data.branding.colors.primary || '#1e3a8a',
                secondary: data.branding.colors.secondary || '#3b82f6',
                accent: data.branding.colors.accent || '#10b981',
                background: data.branding.colors.background || '#f9fafb',
                surface: data.branding.colors.surface || '#ffffff',
                textPrimary: data.branding.colors.text_primary || '#111827',
                textSecondary: data.branding.colors.text_secondary || '#6b7280',
              }
            : null,
          typography: data.branding.typography
            ? {
                fontFamily: data.branding.typography.font_family || 'Inter',
                headingWeight: data.branding.typography.heading_weight || '600',
                bodyWeight: data.branding.typography.body_weight || '400',
              }
            : null,
          appName: data.branding.app_name || 'Command Bridge',
          pwaShortName: data.branding.pwa_short_name || 'CmdBridge',
          tenantSlug: activeTenant.slug,
          tenantId: data.tenant_id,
        });
      } catch (error) {
        console.error('Failed to load branding:', error);
        // Still mark as loaded for this tenant to prevent retry loops, but
        // reset the branding fields first: showing generic Command Bridge
        // chrome is correct, showing the PREVIOUS jurisdiction's seal and name
        // under this tenant's slug is not (see BRANDING_RESET).
        setBranding({
          ...BRANDING_RESET,
          tenantSlug: activeTenant.slug,
          tenantId: activeTenant.id,
        });
      }
    }

    loadBranding();
  }, [activeTenant?.slug, activeTenant?.id, tenantSlug, setBranding, clearBranding]);
}

export default useBrandingInit;
